import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { formatDateTime12Hour, formatTime12Hour } from "../../../../utils/dateTimeFormat";

const EMPTY_VALUE = "—";

export const actionConfig = {
  updated: { label: "تعديل", color: "warning", icon: <EditIcon fontSize="small" /> },
  deleted: { label: "حذف", color: "error", icon: <DeleteIcon fontSize="small" /> },
  created: { label: "إضافة", color: "success", icon: <AddCircleIcon fontSize="small" /> },
  added_items: { label: "إضافة عناصر", color: "info", icon: <AddCircleIcon fontSize="small" /> },
};

export const modelTypeLabel = {
  order: "طلب",
  order_item: "عنصر طلب",
  order_items: "عناصر طلب",
};

export const orderTypeLabel = {
  tables: "طاولة",
  delivery: "توصيل",
  takeaway: "تيك أواي",
};

export const statusLabel = {
  pending: "قيد التجهيز",
  in_prepration: "في التحضير",
  in_preparation: "في التحضير",
  preparing: "في التحضير",
  ready: "جاهز",
  completed: "مكتمل",
  delivered: "تم التسليم",
  pickup: "تم الاستلام من العميل",
  finished: "منتهي",
  cancelled: "ملغي",
  canceled: "ملغي",
};

export const roleLabel = {
  admin: "مدير",
  captain: "كابتن",
  client: "عميل",
  user: "مستخدم",
};

export const fieldLabel = {
  id: "المعرّف",
  order_id: "معرّف الطلب",
  order_item_id: "معرّف عنصر الطلب",
  user_id: "معرّف المستخدم",
  customer_id: "معرّف العميل",
  table_id: "رقم الطاولة",
  captain_order_id: "معرّف الكابتن",
  delivery_id: "معرّف التوصيل",
  product_id: "المنتج",
  drink_id: "المشروب",
  quantity: "الكمية",
  price: "السعر",
  total: "الإجمالي",
  open_time: "وقت الفتح",
  close_time: "وقت الإغلاق",
  note: "ملاحظة",
  status: "الحالة",
  type: "النوع الداخلي",
  order_type: "نوع الطلب",
  date: "التاريخ",
  daily_increment: "رقم اليوم",
  monthly_increment: "رقم الشهر",
  general_increment: "رقم عام",
  is_client_order: "طلب من عميل",
  created_at: "وقت الإنشاء",
  updated_at: "وقت التحديث",
  before_count: "العدد قبل",
  after_count: "العدد بعد",
  old: "قبل",
  new: "بعد",
  added: "العناصر المضافة",
};

const changeItemFields = [
  { key: "id", label: "معرّف عنصر العملية", minWidth: 140 },
  { key: "order_id", label: "طلب عنصر العملية", minWidth: 140 },
  { key: "product_id", label: "منتج العملية", minWidth: 150 },
  { key: "drink_id", label: "مشروب العملية", minWidth: 150 },
  { key: "quantity", label: "كمية العملية", minWidth: 120 },
  { key: "price", label: "سعر العملية", minWidth: 120 },
  { key: "total", label: "إجمالي العملية", minWidth: 130 },
  { key: "open_time", label: "فتح العملية", minWidth: 125 },
  { key: "close_time", label: "إغلاق العملية", minWidth: 125 },
  { key: "note", label: "ملاحظة العملية", minWidth: 170 },
  { key: "status", label: "حالة العملية", minWidth: 130 },
  { key: "created_at", label: "إنشاء العملية", minWidth: 180 },
  { key: "updated_at", label: "تحديث العملية", minWidth: 180 },
];

const toArabicLabel = (value, map) => map[value] || value;

export const formatDateOnly = (dateString) => {
  if (!dateString) return EMPTY_VALUE;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return String(dateString);
  return d.toLocaleDateString("ar-EG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const parseChanges = (changes) => {
  if (!changes) return null;
  if (typeof changes === "object") return changes;

  try {
    return JSON.parse(changes);
  } catch {
    try {
      return JSON.parse(String(changes).replace(/\\(?!["\\/bfnrtu])/g, ""));
    } catch {
      return null;
    }
  }
};

const isPlainObject = (value) => (
  value !== null && typeof value === "object" && !Array.isArray(value)
);

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const normalizeId = (value) => String(value);

const getMappedName = (value, key, productNamesById, drinkNamesById) => {
  if (!hasValue(value)) return null;

  if (key === "product_id") {
    return productNamesById[normalizeId(value)] || `ID: ${value}`;
  }

  if (key === "drink_id") {
    return drinkNamesById[normalizeId(value)] || `ID: ${value}`;
  }

  return null;
};

const formatChangeValue = (value, key = "", productNamesById = {}, drinkNamesById = {}) => {
  if (!hasValue(value)) return EMPTY_VALUE;

  const mappedName = getMappedName(value, key, productNamesById, drinkNamesById);
  if (mappedName) return mappedName;

  if (key === "open_time" || key === "close_time" || key === "received_at" || key === "finished_at") {
    return formatTime12Hour(value, { empty: EMPTY_VALUE });
  }

  if (key === "created_at" || key === "updated_at") {
    return formatDateTime12Hour(value, { empty: EMPTY_VALUE });
  }

  if (key === "date") {
    return formatDateOnly(value);
  }

  if (key === "is_active" || key === "is_client_order") {
    return Number(value) === 1 || value === true ? "نعم" : "لا";
  }

  if (typeof value === "string") {
    return statusLabel[value] || orderTypeLabel[value] || roleLabel[value] || actionConfig[value]?.label || value;
  }

  if (typeof value === "boolean") return value ? "نعم" : "لا";

  if (isPlainObject(value) || Array.isArray(value)) {
    return JSON.stringify(value);
  }

  return String(value);
};

const formatObjectDetails = (obj, productNamesById, drinkNamesById) => {
  if (!isPlainObject(obj)) return EMPTY_VALUE;

  const lines = Object.entries(obj)
    .map(([key, value]) => `${fieldLabel[key] || key}: ${formatChangeValue(value, key, productNamesById, drinkNamesById)}`);

  return lines.length ? lines.join("\n") : EMPTY_VALUE;
};

const collectObjectKeys = (...objects) => {
  const keys = new Set();
  objects.forEach((obj) => {
    if (!isPlainObject(obj)) return;
    Object.keys(obj).forEach((key) => keys.add(key));
  });
  return [...keys];
};

const normalizeChangeItems = (changes) => {
  if (!isPlainObject(changes)) return [];

  if (Array.isArray(changes.added)) {
    return changes.added.filter(isPlainObject);
  }

  if ("old" in changes || "new" in changes) {
    if (isPlainObject(changes.new)) return [changes.new];
    if (isPlainObject(changes.old)) return [changes.old];
  }

  return [];
};

const formatChangeItemField = (items, key, productNamesById, drinkNamesById) => {
  if (!items.length) return EMPTY_VALUE;

  const values = items.map((item, index) => {
    const value = formatChangeValue(item?.[key], key, productNamesById, drinkNamesById);
    return items.length > 1 ? `عنصر ${index + 1}: ${value}` : value;
  });

  return values.join("\n");
};

const getChangeInfo = (log, productNamesById, drinkNamesById) => {
  const changes = parseChanges(log.changes);
  const actionLabel = actionConfig[log.action]?.label || log.action || EMPTY_VALUE;
  const modelLabel = modelTypeLabel[log.model_type] || log.model_type || EMPTY_VALUE;
  const changeItems = normalizeChangeItems(changes);

  const info = {
    summary: `${actionLabel} - ${modelLabel}`,
    fields: EMPTY_VALUE,
    before: EMPTY_VALUE,
    after: EMPTY_VALUE,
    beforeCount: EMPTY_VALUE,
    afterCount: EMPTY_VALUE,
    changeItems,
  };

  if (!isPlainObject(changes)) {
    return info;
  }

  const beforeAfterFields = Object.entries(changes).filter(([, value]) => (
    isPlainObject(value) && ("old" in value || "new" in value)
  ));

  if (beforeAfterFields.length) {
    info.fields = beforeAfterFields.map(([key]) => fieldLabel[key] || key).join("، ");
    info.before = beforeAfterFields
      .map(([key, value]) => `${fieldLabel[key] || key}: ${formatChangeValue(value.old, key, productNamesById, drinkNamesById)}`)
      .join("\n");
    info.after = beforeAfterFields
      .map(([key, value]) => `${fieldLabel[key] || key}: ${formatChangeValue(value.new, key, productNamesById, drinkNamesById)}`)
      .join("\n");
    return info;
  }

  if ("old" in changes || "new" in changes) {
    const fieldKeys = collectObjectKeys(changes.old, changes.new);
    const isDeletedObject = changes.new == null && isPlainObject(changes.old);

    info.fields = fieldKeys.length ? fieldKeys.map((key) => fieldLabel[key] || key).join("، ") : EMPTY_VALUE;
    info.before = isDeletedObject ? EMPTY_VALUE : formatObjectDetails(changes.old, productNamesById, drinkNamesById);
    info.after = isDeletedObject ? "تم الحذف" : formatObjectDetails(changes.new, productNamesById, drinkNamesById);
    return info;
  }

  if (Array.isArray(changes.added)) {
    info.fields = changes.added.length
      ? collectObjectKeys(...changes.added).map((key) => fieldLabel[key] || key).join("، ")
      : EMPTY_VALUE;
    info.beforeCount = formatChangeValue(changes.before_count, "before_count", productNamesById, drinkNamesById);
    info.afterCount = formatChangeValue(changes.after_count, "after_count", productNamesById, drinkNamesById);
    return info;
  }

  const directFields = Object.entries(changes).filter(([, value]) => hasValue(value));
  info.fields = directFields.length ? directFields.map(([key]) => fieldLabel[key] || key).join("، ") : EMPTY_VALUE;
  info.after = directFields.length
    ? directFields
      .map(([key, value]) => `${fieldLabel[key] || key}: ${formatChangeValue(value, key, productNamesById, drinkNamesById)}`)
      .join("\n")
    : EMPTY_VALUE;
  return info;
};

const getOrderItemName = (orderItem, productNamesById, drinkNamesById) => {
  if (orderItem?.drink_id) return drinkNamesById[normalizeId(orderItem.drink_id)] || `ID: ${orderItem.drink_id}`;
  if (orderItem?.product_id) return productNamesById[normalizeId(orderItem.product_id)] || `ID: ${orderItem.product_id}`;
  return EMPTY_VALUE;
};

const cellValue = (value) => (hasValue(value) ? value : EMPTY_VALUE);

const rowColorByAction = {
  updated: "warning",
  deleted: "error",
  created: "success",
  added_items: "info",
};

const getActionRowSx = (action) => (theme) => {
  const paletteKey = rowColorByAction[action] || "grey";
  const paletteColor = paletteKey === "grey" ? theme.palette.grey[500] : theme.palette[paletteKey].main;
  const backgroundColor = alpha(paletteColor, 0.08);
  const hoverColor = alpha(paletteColor, 0.14);

  return {
    "& > td": {
      backgroundColor,
      transition: "background-color 120ms ease",
    },
    "&:hover > td": {
      backgroundColor: hoverColor,
    },
  };
};

export const createOrderLogColumns = ({ productNamesById = {}, drinkNamesById = {} } = {}) => {
  const changeInfoCache = new WeakMap();
  const getCachedChangeInfo = (log) => {
    if (!log || typeof log !== "object") {
      return getChangeInfo(log || {}, productNamesById, drinkNamesById);
    }

    if (!changeInfoCache.has(log)) {
      changeInfoCache.set(log, getChangeInfo(log, productNamesById, drinkNamesById));
    }

    return changeInfoCache.get(log);
  };

  return [
  {
    key: "serial",
    label: "رقم مسلسل",
    description: "ترتيب السجل داخل النتائج المعروضة حاليًا",
    minWidth: 110,
    getValue: (log, index) => index + 1,
  },

  {
    key: "action",
    label: "العملية",
    description: "نوع الحركة المسجلة: إضافة أو تعديل أو حذف",
    minWidth: 130,
    getValue: (log) => actionConfig[log.action]?.label || cellValue(log.action),
    render: (log) => {
      const action = actionConfig[log.action] || { label: cellValue(log.action), color: "default", icon: null };
      return <Chip icon={action.icon} label={action.label} color={action.color} size="small" sx={{ fontWeight: 700 }} />;
    },
  },
  {
    key: "model_type",
    label: "نوع السجل",
    description: "الكيان الذي تمت عليه العملية",
    minWidth: 130,
    getValue: (log) => modelTypeLabel[log.model_type] || cellValue(log.model_type),
    render: (log) => (
      <Chip
        label={modelTypeLabel[log.model_type] || cellValue(log.model_type)}
        size="small"
        variant="outlined"
        color="primary"
      />
    ),
  },
  {
    key: "order_general_increment",
    label: "رقم الطلب",
    description: "الرقم العام الظاهر للطلب",
    minWidth: 110,
    getValue: (log) => log.order ? `#${log.order.general_increment}` : EMPTY_VALUE,
  },
  {
    key: "order_type",
    label: "نوع الطلب",
    description: "طاولة أو توصيل أو تيك أواي",
    minWidth: 120,
    getValue: (log) => log.order?.order_type ? toArabicLabel(log.order.order_type, orderTypeLabel) : EMPTY_VALUE,
  },
  {
    key: "field",
    label: "الحقل",
    description: "الحقل المباشر المرتبط بالعملية إن رجع من API",
    minWidth: 130,
    getValue: (log) => log.field ? (fieldLabel[log.field] || log.field) : EMPTY_VALUE,
  },
  {
    key: "change_summary",
    label: "ملخص التغيير",
    description: "قراءة مختصرة لغرض العملية حسب نوعها",
    minWidth: 170,
    getValue: (log) => getCachedChangeInfo(log).summary,
  },
  {
    key: "changed_fields",
    label: "الحقول المتأثرة",
    description: "أسماء الحقول الموجودة داخل بيانات التغيير",
    minWidth: 240,
    getValue: (log) => getCachedChangeInfo(log).fields,
  },
  {
    key: "change_before",
    label: "قبل",
    description: "القيم القديمة أو بيانات العنصر قبل الحذف",
    minWidth: 260,
    getValue: (log) => getCachedChangeInfo(log).before,
  },
  {
    key: "change_after",
    label: "بعد",
    description: "القيم الجديدة أو بيانات العملية بعد التعديل",
    minWidth: 260,
    getValue: (log) => getCachedChangeInfo(log).after,
  },
  {
    key: "before_count",
    label: "العدد قبل",
    description: "عدد عناصر الطلب قبل عملية إضافة العناصر",
    minWidth: 110,
    getValue: (log) => getCachedChangeInfo(log).beforeCount,
  },
  {
    key: "after_count",
    label: "العدد بعد",
    description: "عدد عناصر الطلب بعد عملية إضافة العناصر",
    minWidth: 110,
    getValue: (log) => getCachedChangeInfo(log).afterCount,
  },
  ...changeItemFields.map((field) => ({
    key: `change_item_${field.key}`,
    label: field.label,
    description: `${fieldLabel[field.key] || field.key} داخل العنصر المضاف أو المحذوف الراجع من changes`,
    minWidth: field.minWidth,
    getValue: (log) => formatChangeItemField(
      getCachedChangeInfo(log).changeItems,
      field.key,
      productNamesById,
      drinkNamesById
    ),
  })),
  {
    key: "order_id",
    label: "Order ID",
    description: "معرّف الطلب الراجع مباشرة من السجل",
    minWidth: 100,
    getValue: (log) => cellValue(log.order_id),
  },
  {
    key: "order_relation_id",
    label: "ID الطلب الداخلي",
    description: "معرّف الطلب داخل كائن order المحمل من API",
    minWidth: 120,
    getValue: (log) => cellValue(log.order?.id),
  },
  {
    key: "order_daily_increment",
    label: "رقم اليوم",
    description: "ترتيب الطلب اليومي",
    minWidth: 100,
    getValue: (log) => cellValue(log.order?.daily_increment),
  },
  {
    key: "order_monthly_increment",
    label: "رقم الشهر",
    description: "ترتيب الطلب الشهري",
    minWidth: 100,
    getValue: (log) => cellValue(log.order?.monthly_increment),
  },
  {
    key: "order_internal_type",
    label: "نوع داخلي",
    description: "قيمة type الخاصة بالطلب كما رجعت من API",
    minWidth: 120,
    getValue: (log) => cellValue(log.order?.type),
  },
  {
    key: "order_status",
    label: "حالة الطلب",
    description: "آخر حالة محفوظة على الطلب",
    minWidth: 130,
    getValue: (log) => log.order?.status ? toArabicLabel(log.order.status, statusLabel) : EMPTY_VALUE,
  },
  {
    key: "order_total",
    label: "إجمالي الطلب",
    description: "قيمة total الخاصة بالطلب",
    minWidth: 120,
    getValue: (log) => cellValue(log.order?.total),
  },
  {
    key: "order_date",
    label: "تاريخ الطلب",
    description: "تاريخ الطلب",
    minWidth: 130,
    getValue: (log) => formatDateOnly(log.order?.date),
  },
  {
    key: "order_open_time",
    label: "فتح الطلب",
    description: "وقت فتح الطلب بنظام 12 ساعة",
    minWidth: 120,
    getValue: (log) => formatTime12Hour(log.order?.open_time, { empty: EMPTY_VALUE }),
  },
  {
    key: "order_close_time",
    label: "إغلاق الطلب",
    description: "وقت إغلاق الطلب بنظام 12 ساعة",
    minWidth: 120,
    getValue: (log) => formatTime12Hour(log.order?.close_time, { empty: EMPTY_VALUE }),
  },
  {
    key: "order_customer_id",
    label: "Customer ID",
    description: "معرّف العميل المرتبط بالطلب",
    minWidth: 110,
    getValue: (log) => cellValue(log.order?.customer_id),
  },
  {
    key: "order_table_id",
    label: "الطاولة",
    description: "رقم الطاولة المرتبط بالطلب",
    minWidth: 90,
    getValue: (log) => cellValue(log.order?.table_id),
  },
  {
    key: "order_captain_id",
    label: "Captain ID",
    description: "معرّف الكابتن المرتبط بالطلب",
    minWidth: 110,
    getValue: (log) => cellValue(log.order?.captain_order_id),
  },
  {
    key: "order_user_id",
    label: "Order User ID",
    description: "معرّف المستخدم داخل بيانات الطلب",
    minWidth: 120,
    getValue: (log) => cellValue(log.order?.user_id),
  },
  {
    key: "order_delivery_id",
    label: "Delivery ID",
    description: "معرّف مندوب التوصيل إن وجد",
    minWidth: 110,
    getValue: (log) => cellValue(log.order?.delivery_id),
  },
  {
    key: "is_client_order",
    label: "طلب عميل",
    description: "هل الطلب قادم من العميل مباشرة",
    minWidth: 100,
    getValue: (log) => formatChangeValue(log.order?.is_client_order, "is_client_order", productNamesById, drinkNamesById),
  },
  {
    key: "order_created_at",
    label: "إنشاء الطلب",
    description: "وقت إنشاء الطلب بنظام 12 ساعة",
    minWidth: 180,
    getValue: (log) => formatDateTime12Hour(log.order?.created_at, { empty: EMPTY_VALUE }),
  },
  {
    key: "order_updated_at",
    label: "تحديث الطلب",
    description: "وقت آخر تحديث للطلب بنظام 12 ساعة",
    minWidth: 180,
    getValue: (log) => formatDateTime12Hour(log.order?.updated_at, { empty: EMPTY_VALUE }),
  },
  {
    key: "order_item_id",
    label: "Order Item ID",
    description: "معرّف عنصر الطلب الراجع مباشرة من السجل",
    minWidth: 120,
    getValue: (log) => cellValue(log.order_item_id),
  },
  {
    key: "item_relation_id",
    label: "ID العنصر",
    description: "معرّف عنصر الطلب من العلاقة المحملة",
    minWidth: 110,
    getValue: (log) => cellValue(log.order_item?.id),
  },
  {
    key: "item_name",
    label: "اسم الصنف",
    description: "اسم المنتج أو مشروب AI حسب المعرّف",
    minWidth: 180,
    getValue: (log) => getOrderItemName(log.order_item, productNamesById, drinkNamesById),
  },
  {
    key: "item_product_id",
    label: "Product ID",
    description: "معرّف المنتج داخل عنصر الطلب",
    minWidth: 110,
    getValue: (log) => cellValue(log.order_item?.product_id),
  },
  {
    key: "item_drink_id",
    label: "Drink ID",
    description: "معرّف مشروب AI داخل عنصر الطلب",
    minWidth: 100,
    getValue: (log) => cellValue(log.order_item?.drink_id),
  },
  {
    key: "item_quantity",
    label: "كمية الصنف",
    description: "كمية عنصر الطلب",
    minWidth: 110,
    getValue: (log) => cellValue(log.order_item?.quantity),
  },
  {
    key: "item_price",
    label: "سعر الصنف",
    description: "سعر عنصر الطلب",
    minWidth: 110,
    getValue: (log) => cellValue(log.order_item?.price),
  },
  {
    key: "item_total",
    label: "إجمالي الصنف",
    description: "إجمالي عنصر الطلب",
    minWidth: 120,
    getValue: (log) => cellValue(log.order_item?.total),
  },
  {
    key: "item_status",
    label: "حالة الصنف",
    description: "حالة عنصر الطلب",
    minWidth: 120,
    getValue: (log) => log.order_item?.status ? toArabicLabel(log.order_item.status, statusLabel) : EMPTY_VALUE,
  },
  {
    key: "item_note",
    label: "ملاحظة الصنف",
    description: "ملاحظات عنصر الطلب",
    minWidth: 180,
    getValue: (log) => cellValue(log.order_item?.note),
  },
  {
    key: "item_open_time",
    label: "فتح الصنف",
    description: "وقت فتح عنصر الطلب بنظام 12 ساعة",
    minWidth: 120,
    getValue: (log) => formatTime12Hour(log.order_item?.open_time, { empty: EMPTY_VALUE }),
  },
  {
    key: "item_close_time",
    label: "إغلاق الصنف",
    description: "وقت إغلاق عنصر الطلب بنظام 12 ساعة",
    minWidth: 120,
    getValue: (log) => formatTime12Hour(log.order_item?.close_time, { empty: EMPTY_VALUE }),
  },
  {
    key: "item_created_at",
    label: "إنشاء الصنف",
    description: "وقت إنشاء عنصر الطلب بنظام 12 ساعة",
    minWidth: 180,
    getValue: (log) => formatDateTime12Hour(log.order_item?.created_at, { empty: EMPTY_VALUE }),
  },
  {
    key: "item_updated_at",
    label: "تحديث الصنف",
    description: "وقت تحديث عنصر الطلب بنظام 12 ساعة",
    minWidth: 180,
    getValue: (log) => formatDateTime12Hour(log.order_item?.updated_at, { empty: EMPTY_VALUE }),
  },
  {
    key: "log_user_id",
    label: "User ID",
    description: "معرّف المستخدم الراجع مباشرة من السجل",
    minWidth: 90,
    getValue: (log) => cellValue(log.user_id),
  },
  {
    key: "user_relation_id",
    label: "ID المستخدم الداخلي",
    description: "معرّف المستخدم داخل كائن user المحمل من API",
    minWidth: 140,
    getValue: (log) => cellValue(log.user?.id),
  },
  {
    key: "user_name",
    label: "المستخدم",
    description: "اسم المستخدم الذي نفذ العملية",
    minWidth: 160,
    getValue: (log) => cellValue(log.user?.name),
  },
  {
    key: "username",
    label: "اسم الدخول",
    description: "username الخاص بالمستخدم",
    minWidth: 130,
    getValue: (log) => cellValue(log.user?.username),
  },
  {
    key: "user_role",
    label: "الدور",
    description: "صلاحية أو دور المستخدم",
    minWidth: 110,
    getValue: (log) => log.user?.role ? toArabicLabel(log.user.role, roleLabel) : EMPTY_VALUE,
  },
  {
    key: "user_type",
    label: "نوع المستخدم",
    description: "قيمة type الخاصة بالمستخدم",
    minWidth: 120,
    getValue: (log) => log.user?.type ? toArabicLabel(log.user.type, roleLabel) : EMPTY_VALUE,
  },
  {
    key: "user_active",
    label: "نشط",
    description: "حالة تفعيل حساب المستخدم",
    minWidth: 90,
    getValue: (log) => formatChangeValue(log.user?.is_active, "is_active", productNamesById, drinkNamesById),
  },
  {
    key: "user_email",
    label: "البريد",
    description: "البريد الإلكتروني للمستخدم إن وجد",
    minWidth: 170,
    getValue: (log) => cellValue(log.user?.email),
  },
  {
    key: "user_phone",
    label: "الهاتف 1",
    description: "رقم الهاتف الأساسي للمستخدم",
    minWidth: 130,
    getValue: (log) => cellValue(log.user?.phone),
  },
  {
    key: "user_phone2",
    label: "الهاتف 2",
    description: "رقم الهاتف الثاني للمستخدم",
    minWidth: 130,
    getValue: (log) => cellValue(log.user?.phone2),
  },
  {
    key: "user_phone3",
    label: "الهاتف 3",
    description: "رقم الهاتف الثالث للمستخدم",
    minWidth: 130,
    getValue: (log) => cellValue(log.user?.phone3),
  },
  {
    key: "user_address",
    label: "العنوان 1",
    description: "العنوان الأساسي للمستخدم",
    minWidth: 160,
    getValue: (log) => cellValue(log.user?.address),
  },
  {
    key: "user_address2",
    label: "العنوان 2",
    description: "العنوان الثاني للمستخدم",
    minWidth: 160,
    getValue: (log) => cellValue(log.user?.address2),
  },
  {
    key: "user_address3",
    label: "العنوان 3",
    description: "العنوان الثالث للمستخدم",
    minWidth: 160,
    getValue: (log) => cellValue(log.user?.address3),
  },
  {
    key: "user_created_at",
    label: "إنشاء المستخدم",
    description: "وقت إنشاء حساب المستخدم بنظام 12 ساعة",
    minWidth: 180,
    getValue: (log) => formatDateTime12Hour(log.user?.created_at, { empty: EMPTY_VALUE }),
  },
  {
    key: "user_updated_at",
    label: "تحديث المستخدم",
    description: "وقت تحديث حساب المستخدم بنظام 12 ساعة",
    minWidth: 180,
    getValue: (log) => formatDateTime12Hour(log.user?.updated_at, { empty: EMPTY_VALUE }),
  },
  {
    key: "log_created_at",
    label: "وقت السجل",
    description: "وقت إنشاء سجل العملية بنظام 12 ساعة",
    minWidth: 180,
    getValue: (log) => formatDateTime12Hour(log.created_at, { empty: EMPTY_VALUE }),
  },
  {
    key: "log_updated_at",
    label: "تحديث السجل",
    description: "وقت تحديث سجل العملية بنظام 12 ساعة",
    minWidth: 180,
    getValue: (log) => formatDateTime12Hour(log.updated_at, { empty: EMPTY_VALUE }),
  },
  ];
};

export const OrderLogTableHead = ({ columns, headSx }) => (
  <TableRow>
    {columns.map((column) => (
      <TableCell
        key={column.key}
        align="center"
        sx={{ ...headSx, minWidth: column.minWidth }}
      >
        <Tooltip title={column.description || ""} arrow placement="top">
          <Box component="span">{column.label}</Box>
        </Tooltip>
      </TableCell>
    ))}
  </TableRow>
);

export const OrderLogTableRow = ({ log, columns, rowIndex }) => (
  <TableRow hover sx={getActionRowSx(log.action)}>
    {columns.map((column) => (
      <TableCell
        key={column.key}
        align="center"
        sx={{
          minWidth: column.minWidth,
          maxWidth: Math.max(column.minWidth || 120, 220),
          whiteSpace: "pre-line",
          overflowWrap: "anywhere",
          verticalAlign: "top",
        }}
      >
        {column.render ? column.render(log) : (
          <Typography variant="body2" component="span">
            {column.getValue(log, rowIndex)}
          </Typography>
        )}
      </TableCell>
    ))}
  </TableRow>
);

export const buildOrderLogExportRows = (logs, columns) => (
  logs.map((log, index) => columns.reduce((row, column) => {
    row[column.label] = column.getValue(log, index);
    return row;
  }, {}))
);

export const getOrderLogSearchText = (log, columns, rowIndex = 0) => (
  columns
    .map((column) => column.getValue(log, rowIndex))
    .concat(log.changes || "")
    .filter((value) => value !== null && value !== undefined)
    .join(" ")
    .toLowerCase()
);
